import React from 'react'
import { CheckCircle, Car, DollarSign, Settings, FileText, Image } from 'lucide-react'
import { SummarySection, SummaryItem, SummaryPriceCard, SummaryGrid, InfoBox } from '../../ui'
import { StepCard } from '../../form'
import { useTranslation } from 'react-i18next'

interface VehicleRegistrationStep6SummaryProps {
  formData: {
    name: string
    brandName: string
    modelName: string
    condition: string
    modelYear: string
    manufactureYear: string
    color: string
    mileage: string
    price: string
    minimumPrice: string
    purchasePrice: string
    status: string
    fuelType?: string
    transmission?: string
    type?: string
    doors?: string
    steering?: string
    busBodyBrand?: string
    busBodyModel?: string
    busOperationType?: string
    busFleetNumber?: string
    plate?: string
    chassis?: string
    renavam?: string
    isInspected?: boolean
    inspectionDate?: string
    isAuction?: boolean
    description?: string
    photos?: File[]
    videoUrl?: string
  }
  vehicleOptions: {
    conditions?: Array<{ value: string; label: string }>
    statuses?: Array<{ value: string; label: string }>
    colors?: Array<{ value: string; label: string }>
    fuelTypes?: Array<{ value: string; label: string }>
    transmissions?: Array<{ value: string; label: string }>
    bodyTypes?: Array<{ value: string; label: string }>
    steeringTypes?: Array<{ value: string; label: string }>
  }
}

const getLabel = (options: Array<{ value: string; label: string }> | undefined, value: string | undefined): string => {
  if (!value) return ''
  return options?.find((item) => item.value === value)?.label || value
}

export const VehicleRegistrationStep6Summary: React.FC<VehicleRegistrationStep6SummaryProps> = ({
  formData,
  vehicleOptions,
}) => {
  const { t } = useTranslation()
  const condition = typeof formData.condition === 'string' ? formData.condition : ''
  const status = typeof formData.status === 'string' ? formData.status : ''
  const photoCount = Array.isArray(formData.photos) ? formData.photos.length : 0

  return (
    <StepCard
      title={t('vehicleRegistration.summary.title')}
      icon={CheckCircle}
      description={t('vehicleRegistration.summary.description')}
    >
      <InfoBox variant="success">
        <strong>{t('vehicleRegistration.summary.readyTitle')}</strong> {t('vehicleRegistration.summary.readyMessage')}
      </InfoBox>

      <SummarySection title={t('vehicleRegistration.summary.basicSectionTitle')} icon={Car}>
        <SummaryGrid columns={2}>
          <SummaryItem label={t('common.name')} value={(formData.name as string) || ''} />
          <SummaryItem label={t('common.brand')} value={(formData.brandName as string) || ''} />
          <SummaryItem label={t('common.model')} value={(formData.modelName as string) || ''} />
          <SummaryItem label={t('common.condition')} value={getLabel(vehicleOptions.conditions, condition)} />
          <SummaryItem
            label={t('vehicleRegistration.summary.modelYearAndManufactureYearLabel')}
            value={`${(formData.modelYear as string) || ''} / ${(formData.manufactureYear as string) || ''}`}
          />
          <SummaryItem label={t('common.color')} value={getLabel(vehicleOptions.colors, formData.color) || t('common.notProvided')} />
          <SummaryItem label={t('common.mileage')} value={`${(formData.mileage as string) || ''} ${t('common.km')}`} />
        </SummaryGrid>
      </SummarySection>

      <SummarySection title={t('vehicleRegistration.summary.valuesSectionTitle')} icon={DollarSign} iconColor="text-emerald-600">
        <SummaryGrid columns={3}>
          <SummaryPriceCard label={t('common.salePrice')} value={(formData.price as string) || ''} variant="sale" />
          <SummaryPriceCard
            label={t('vehicleRegistration.summary.minimumPriceLabel')}
            value={(formData.minimumPrice as string) || t('common.notProvided')}
            variant="minimum"
          />
          <SummaryPriceCard
            label={t('common.purchasePrice')}
            value={(formData.purchasePrice as string) || t('common.notProvided')}
            variant="purchase"
          />
        </SummaryGrid>

        <SummaryItem label={t('common.status')} value={getLabel(vehicleOptions.statuses, status)} />
      </SummarySection>

      <SummarySection title={t('vehicleRegistration.summary.technicalSectionTitle')} icon={Settings} iconColor="text-blue-600">
        <SummaryGrid columns={2}>
          {(formData.fuelType as string) && (
            <SummaryItem label={t('common.fuel')} value={getLabel(vehicleOptions.fuelTypes, formData.fuelType)} />
          )}
          {(formData.transmission as string) && (
            <SummaryItem label={t('common.transmission')} value={getLabel(vehicleOptions.transmissions, formData.transmission)} />
          )}
          {(formData.type as string) && (
            <SummaryItem label={t('common.type')} value={getLabel(vehicleOptions.bodyTypes, formData.type)} />
          )}
          {(formData.doors as string) && <SummaryItem label={t('common.doors')} value={formData.doors as string} />}
          {(formData.steering as string) && (
            <SummaryItem label={t('common.steering')} value={getLabel(vehicleOptions.steeringTypes, formData.steering)} />
          )}
        </SummaryGrid>
        {condition === 'D' && (
          <SummaryGrid columns={2}>
            <SummaryItem label={t('common.bodyBrand')} value={(formData.busBodyBrand as string) || t('common.notProvided')} />
            <SummaryItem label={t('common.bodyModel')} value={(formData.busBodyModel as string) || t('common.notProvided')} />
            <SummaryItem label={t('common.operation')} value={(formData.busOperationType as string) || t('common.notProvided')} />
            <SummaryItem label={t('common.fleetNumber')} value={(formData.busFleetNumber as string) || t('common.notProvided')} />
          </SummaryGrid>
        )}
      </SummarySection>

      {(formData.plate || formData.chassis || formData.renavam) && (
        <SummarySection title={t('vehicleRegistration.summary.documentationSectionTitle')} icon={FileText} iconColor="text-purple-600">
          <SummaryGrid columns={3}>
            {(formData.plate as string) && <SummaryItem label={t('vehicleRegistration.documentation.plateLabel')} value={formData.plate as string} />}
            {(formData.chassis as string) && <SummaryItem label={t('vehicleRegistration.documentation.chassisLabel')} value={formData.chassis as string} />}
            {(formData.renavam as string) && <SummaryItem label={t('vehicleRegistration.documentation.renavamLabel')} value={formData.renavam as string} />}
          </SummaryGrid>

          {(formData.isInspected as boolean) && (
            <SummaryItem
              label={t('vehicleRegistration.documentation.inspectionTitle')}
              value={`${t('vehicleRegistration.documentation.inspectedBadge')} em ${formData.inspectionDate ? new Date(formData.inspectionDate as string).toLocaleDateString('pt-BR') : t('common.notProvided')}`}
            />
          )}

          {(formData.isAuction as boolean) && <SummaryItem label={t('vehicleRegistration.documentation.auctionTitle')} value={t('vehicleRegistration.documentation.auctionLabel')} />}
        </SummarySection>
      )}

      {(formData.description as string) && (
        <SummarySection title={t('vehicleRegistration.summary.descriptionSectionTitle')} icon={FileText} iconColor="text-indigo-600">
          <SummaryItem label={t('vehicleRegistration.documentation.descriptionTitle')} value={formData.description as string} />
        </SummarySection>
      )}

      {(Array.isArray(formData.photos) && (formData.photos as File[]).length > 0) || (formData.videoUrl as string) ? (
        <SummarySection title={t('vehicleRegistration.summary.mediaSectionTitle')} icon={Image} iconColor="text-pink-600">
          {photoCount > 0 && (
            <SummaryItem
              label={t('common.photos')}
              value={photoCount === 1 ? t('vehicleRegistration.summary.photoSelectedOne') : t('vehicleRegistration.summary.photoSelectedMany', { count: photoCount })}
            />
          )}
          {(formData.videoUrl as string) && <SummaryItem label={t('common.video')} value={formData.videoUrl as string} />}
        </SummarySection>
      ) : null}

      <InfoBox variant="warning">
        <strong>{t('common.warning')}:</strong> {t('vehicleRegistration.summary.warningMessage')}
      </InfoBox>
    </StepCard>
  )
}

export { VehicleRegistrationStep6Summary as VehicleEditStep6Summary }
